// Split out of src/components/ChildView.jsx (docs/FILE_SPLIT_RUN1_SPEC.md, move-only). ChildView.jsx
// re-exports everything it exported before, so existing imports keep working.

import { Celebration, ChildMark, Icon, OinkIcon, PARTY_PIECE_SETS, Piggington, characterName } from "../Common.jsx";
import { AVATAR_FRAME_STYLES, CHARACTER_CATALOG, OINK_CATALOG, OINK_GIFT_MIN, OINK_GIFT_PRESETS, OINK_SLOTS, oinkGiftCheck, PIGGINGTON_WARDROBE_SLOTS, PIG_NAME_MAX_LENGTH, childOinks, equippedCharacter, equippedCharacterBaseId, equippedCosmetic, ownedCharacters, ownedCosmetics, pigDisplayNameFor, pigLineFor, wornPiggingtonCosmetics, oinksToGo } from "../../lib/oinks.js";
import { t as ct } from "../../lib/content.js";
import { useBackLayer } from "../../lib/backstack.js";
import { isYoungChild } from "../../lib/age.js";
import { currentHabitStreak, dateStrAddDays, fmtNumber, goalProgress, goalTargetTokens, habitsForChild, isHabitDayComplete, todayStr } from "../../lib/utils.js";

const { useState, useEffect, useCallback, useMemo, useRef, useId } = React;

/* --------------------------- Piggington & the Oink Shop --------------------------- */

// Piggington — the real vector mascot — was defined here from Aug 2026 until R5, when it
// moved to Common.jsx (imported above) so that SnackCatch.jsx (Piggington's Arcade) could
// use the same component without creating a ChildView<->SnackCatch import cycle. Everything
// below still renders it exactly as before.

// Derives a mood expression from signals PigCard has to hand. R4 finish (2026-08): now also
// takes transient/situational overrides so three more of the sprite's seven expressions get
// real triggers, not just the daily habit baseline:
//   - badgeCelebrate (a Trophy Cabinet badge just unlocked, ~3.2s window) -> celebrating
//   - perfectWeekCelebrate (Aug 2026: the Daily Question answered all 7 days this week,
//     ~3.2s window, same shape as badgeCelebrate) -> celebrating
//   - goalReached (the top goal is fully funded) -> wow
//   - capBlocked (this week's spending cap has been hit) -> oops
// Priority: celebrating > wow > oops > the existing habit-based happy/sleepy/neutral. All
// overrides are computed fresh from data PigCard/HomeTab already have — no new persisted
// state (perfectWeekCelebrate is the one exception, backed by learningContent.dailyQ —
// see App.jsx's onAnswerDailyQuestion). fx-thinking is used separately, on the Daily
// Question overlay itself.
function piggingtonMoodFor(child, habits, habitLog, overrides = {}) {
  if (overrides.badgeCelebrate || overrides.perfectWeekCelebrate) return "fx-celebrating";
  if (overrides.goalReached) return "fx-wow";
  if (overrides.capBlocked) return "fx-oops";
  const today = todayStr();
  const applicable = habitsForChild(habits, child.id);
  if (applicable.length === 0) return "fx-neutral";
  if (isHabitDayComplete(habitLog, habits, child.id, today)) return "fx-happy";
  if (currentHabitStreak(habitLog, habits, child.id, today) > 0) return "fx-happy";
  for (let back = 1; back <= 3; back++) {
    if (isHabitDayComplete(habitLog, habits, child.id, dateStrAddDays(today, -back))) return "fx-neutral";
  }
  return "fx-sleeping";
}

// The Piggington card at the very top of Kid Home — the app's warm identity moment and the
// single entry point to the Oink Shop. Renders the real vector sprite (design handoff, Aug
// 2026) with a mood expression (piggingtonMoodFor) that now also reacts to real moments —
// badge unlocks, a reached goal, a spending-cap block — not just the daily habit baseline.
// R4 finish (2026-08): the name is now editable (tap it) via pigName on the profile, falling
// back to the age-based default ("Piggles" under 8, "Piggington" from 8 up — pigDisplayName)
// when unset. Hidden entirely when a parent switches Oinks off for this child.

export function PigCard({ child, habits, habitLog, oinksOn, onOpenShop, badgeCelebrate, perfectWeekCelebrate, capBlocked, topGoal, rewardSettings, onChangeAvatar }) {
  const [renaming, setRenaming] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  if (!oinksOn) return null;
  const name = pigDisplayNameFor(child);
  const line = pigLineFor(`${todayStr()}-${child.id}`);
  const goalReached = !!(topGoal && goalProgress(child, goalTargetTokens(topGoal, rewardSettings?.exchangeRate), rewardSettings?.exchangeRate).reached);
  const mood = piggingtonMoodFor(child, habits, habitLog, { badgeCelebrate, perfectWeekCelebrate, goalReached, capBlocked });
  const worn = wornPiggingtonCosmetics(child);
  function startRename() { setNameDraft(child.pigName || ""); setRenaming(true); }
  function saveRename() {
    const trimmed = nameDraft.trim().slice(0, PIG_NAME_MAX_LENGTH);
    onChangeAvatar({ pigName: trimmed || null });
    setRenaming(false);
  }
  return (
    <div data-testid="pig-card" className="kc kc-pig rounded-2xl p-3.5 relative">
      {/* White sheet (9 Sep): the block title is the pig's OWN name (a renamed pig is
          "Piggles" everywhere, including here — spec 12), left-aligned, with the Oink disc.
          The tap-to-rename label under the face is unchanged. */}
      <div className="ks-title kc-pig" style={{ marginBottom: 6 }}><span className="kc-disc" aria-hidden="true"><OinkIcon className="w-4 h-4" /></span><span className="kc-title">{name}</span></div>
      <div className="flex items-center gap-3">
        {/* R6 finish (Alan, Aug 2026): grew from 80px/104px — was leaving a wide dead strip
            on the right on most phone widths, since the text column below was capped at
            max-w-[180px] regardless of how much room the card actually had.
            Follow-up: 108px (+gap-4) turned out to be JUST enough narrower on real devices
            that the Oinks chip + Oink Shop button wrapped to two lines each (Alan's
            screenshot) — trimmed to 100px and gap-3 to give that row the extra ~24px it
            needed, rather than shrinking the chip/button text past comfortable tap-target
            size to make them fit in the old width. */}
        <div className="flex-shrink-0 w-[100px]">
          {/* Alan (7 Sep): this Home section is PIGGINGTON'S — it's his shop, so the card shows
              the shopkeeper himself. The child's dressed character shows on the hero, the name
              line and inside the shop. */}
          <Piggington expression={mood} base="pg-base" className="w-[100px] h-[130px]" />
          {renaming ? (
            <input
              data-testid="pig-rename-input"
              autoFocus
              maxLength={PIG_NAME_MAX_LENGTH}
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              onBlur={saveRename}
              onKeyDown={(e) => { if (e.key === "Enter") saveRename(); if (e.key === "Escape") setRenaming(false); }}
              className="w-full text-center text-base font-extrabold text-slate-700 border border-slate-300 rounded-md -mt-1 px-1 py-0.5"
            />
          ) : (
            <button data-testid="pig-name-label" onClick={startRename} className="w-full text-center text-base font-extrabold text-slate-600 -mt-1" title="Tap to rename">{name}</button>
          )}
        </div>
        {/* Speech bubble now spans the full remaining width instead of capping at 180px —
            that cap (plus the fixed-width pig column above) was the actual cause of the
            dead space to the right; the chip+CTA row below does the same, justify-between
            instead of a left-packed flex-wrap row that stopped partway across. */}
        <div className="flex-1 min-w-0 flex flex-col justify-center">
          <div className="relative bg-white border border-slate-200 rounded-xl px-3 py-2.5 text-sm font-semibold text-slate-700 w-full">
            {line}
          </div>
          {/* whitespace-nowrap + flex-shrink-0 on both pieces (Alan, Aug 2026): without
              these, "21 Oinks" and "Oink Shop ›" each wrapped their own text onto a second
              line once the row got tight — the pig-column trim above buys back some of
              that width, but nowrap is the actual fix, since it holds regardless of exact
              available width rather than depending on getting the pig size exactly right.
              Padding tightened slightly (chip pr-3→pr-2.5, gap-1.5→gap-1; button
              px-4 py-2→px-3 py-1.5) to help both fit comfortably side by side too. */}
          <div className="flex items-center justify-between gap-2 mt-2.5">
            <span data-testid="oink-chip" className="inline-flex items-center gap-1 bg-pink-50 text-pink-600 font-bold text-sm rounded-xl pl-2 pr-2.5 py-1.5 whitespace-nowrap flex-shrink-0">
              <OinkIcon className="w-5 h-5 flex-shrink-0" /> {fmtNumber(childOinks(child))} Oinks
            </span>
            <button data-testid="open-oink-shop" onClick={onOpenShop} className="bg-pink-500 text-white font-bold text-sm rounded-xl px-3 py-1.5 whitespace-nowrap flex-shrink-0">{ct("kid.pig.open_shop", "Oink Shop")}</button>
          </div>

        </div>
      </div>
    </div>
  );
}

// Shared by every unowned, unaffordable tile (cosmetics AND characters) — the "goals page"
// mechanic (oinks-pricing-strategy.md §5): progress, not pressure. Grey styling is applied by
// the caller (the tile's own preview area gets grayscale/opacity so the greying reads on the
// art, not just this bar).
function ShopProgressBar({ balance, price }) {
  const pct = Math.max(0, Math.min(100, Math.round((balance / price) * 100)));
  return (
    <div className="w-full" data-testid="oink-progress">
      <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden mb-1">
        <div className="h-full bg-pink-300 rounded-full" style={{ width: `${pct}%` }} />
      </div>
      <div className="text-xs font-medium text-slate-400">{balance} / {price}</div>
    </div>
  );
}

// ---- LB-247: the receiver's moment — shown once, next time they open Kid Mode ----
export function OinkGiftReceived({ gift, child, onClose }) {
  if (!gift) return null;
  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <Celebration variant="pig" count={22} />
      <div role="dialog" aria-modal="true" data-testid="oink-gift-received" className="bg-white rounded-2xl p-5 w-full max-w-xs shadow-2xl text-center">
        <div className="text-4xl mb-2" aria-hidden="true">✦</div>
        <div className="text-lg font-extrabold text-slate-900">{gift.from
          ? ct("kid.oink_gift.received_title", "{from} sent you {n} Oinks", { from: gift.from, n: gift.amount })
          : ct("kid.oink_gift.received_parent_title", "Your grown-up sent you {n} Oinks", { n: gift.amount })}</div>
        <button onClick={onClose} className="mt-4 w-full bg-navy text-white font-bold rounded-lg py-2.5">{ct("kid.oink_gift.received_cta", "Nice")}</button>
      </div>
    </div>,
    document.body
  );
}

// LB-247 Gifts tab: one tile per visible sibling; tapping opens the amount sheet inline.
function OinkGiftsPanel({ child, siblings, onGift }) {
  const [toId, setToId] = useState(null);
  const [preset, setPreset] = useState(25);
  const [other, setOther] = useState("");
  const today = todayStr();
  const to = siblings.find((p) => p.id === toId);
  const amount = other !== "" ? Math.floor(Number(other) || 0) : preset;
  const check = to ? oinkGiftCheck(child, to, amount, today) : null;
  return (
    <div data-testid="oink-gifts-panel">
      <p className="text-xs text-slate-500 mb-2">{ct("kid.oink_gift.intro", "Send some of your Oinks to a brother or sister. They come out of yours.")}</p>
      {siblings.map((p) => {
        const sentToday = child.oinkGifts && child.oinkGifts[p.id] === today;
        return (
          <div key={p.id} className="bg-white border border-slate-200 rounded-xl p-3 mb-2 flex items-center gap-3">
            <ChildMark profile={p} size="md" />
            <div className="min-w-0 flex-1"><div className="font-extrabold text-sm text-slate-900">{p.name}</div><div className="text-xs text-slate-500">{sentToday ? ct("kid.oink_gift.sent_today", "Sent today. Try again tomorrow") : ct("kid.oink_gift.tile_sub", "Send some of your Oinks")}</div></div>
            {!sentToday && <button onClick={() => { setToId(p.id); setOther(""); setPreset(25); }} className="bg-pink-500 text-white font-extrabold text-sm rounded-lg px-3 py-1.5" data-testid={`oink-gift-${p.id}`}>{ct("kid.oink_gift.tile_cta", "Gift")}</button>}
          </div>
        );
      })}
      {to && (
        <div className="bg-white border border-slate-200 rounded-2xl p-4 mt-2" data-testid="oink-gift-sheet">
          <div className="font-extrabold text-base text-slate-900">{ct("kid.oink_gift.sheet_title", "Give {name} some Oinks", { name: to.name })}</div>
          <p className="text-sm text-slate-500 mb-3">{ct("kid.oink_gift.sheet_sub", "They come out of yours. You have {n}.", { n: childOinks(child) })}</p>
          <div className="flex gap-2 mb-3">
            {OINK_GIFT_PRESETS.map((n) => <button key={n} onClick={() => { setPreset(n); setOther(""); }} className={`flex-1 rounded-lg py-2 font-extrabold text-sm ${other === "" && preset === n ? "bg-navy text-white" : "bg-slate-100 text-slate-700"}`}>{n}</button>)}
            <input inputMode="numeric" placeholder={ct("kid.oink_gift.other", "Other")} value={other} onChange={(e) => setOther(e.target.value.replace(/[^0-9]/g, ""))} className="flex-1 min-w-0 rounded-lg border border-slate-200 px-2 text-sm text-center" />
          </div>
          <button disabled={!check?.ok} onClick={() => { onGift(to.id, amount); setToId(null); }} data-testid="oink-gift-send" className={`w-full rounded-lg py-2.5 font-extrabold text-sm ${check?.ok ? "bg-pink-500 text-white" : "bg-slate-200 text-slate-400"}`}>{ct("kid.oink_gift.send", "Send {n} Oinks", { n: amount || OINK_GIFT_MIN })}</button>
          <button onClick={() => setToId(null)} className="w-full text-center text-sm font-bold text-slate-400 mt-2">{ct("kid.oink_gift.not_now", "Not now")}</button>
        </div>
      )}
    </div>
  );
}

export function OinkShop({ child, onBuy, onEquip, onBuyCharacter, onEquipCharacter, onClose, siblings = [], onGift }) {
  const [slot, setSlot] = useState("characters");
  const balance = childOinks(child);
  const owned = ownedCosmetics(child);
  const ownedChars = ownedCharacters(child);
  const equippedChar = equippedCharacter(child); // null = Piggington, always available
  const worn = wornPiggingtonCosmetics(child);
  const young = isYoungChild(child);
  const isCharactersTab = slot === "characters";
  // Aug 2026 wardrobe expansion: OINK_SLOTS entries are now shop TABS, each spanning one
  // or more item slots (`slots`) — e.g. Clothes = torso + outfit, Face = brows + lashes —
  // so the pill row stays phone-sized despite four new slots. `slot` state holds the TAB
  // id; each item's own `slot` still drives equip conflicts exactly as before. "characters"
  // is the one tab with no `slots` — it sources CHARACTER_CATALOG instead, below.
  const activeTab = OINK_SLOTS.find((s) => s.id === slot) || OINK_SLOTS[0];
  const items = isCharactersTab ? [] : OINK_CATALOG.filter((i) => activeTab.slots.includes(i.slot));
  const pigName = pigDisplayNameFor(child);
  // LB-331: a single tap on a priced item used to debit immediately, no undo — a mis-tap on
  // a Legendary at 650-750 Oinks wiped out weeks of Learn & Earn. Buy now only OPENS this
  // sheet; onBuy/onBuyCharacter (the real debit, in App.jsx) fire only from its own Buy
  // button. Free try-on (Wear it/Take off) and the can't-afford state are untouched — they
  // never called onBuy/onBuyCharacter in the first place.
  const [confirming, setConfirming] = useState(null); // { kind, id, price, label, preview } | null
  const submittingRef = useRef(false); // synchronous, so a double-tap on Buy can never debit twice
  // LB-315: the confirm sheet is one layer on the shared back stack (it used to push its own
  // history entry, the first surface that did; the rule is now everyone's). A tap outside the
  // card and the gesture both close it with the balance unchanged (Alan).
  useBackLayer(!!confirming, () => setConfirming(null));
  function closeConfirm() { setConfirming(null); }
  function openConfirm(payload) {
    submittingRef.current = false;
    setConfirming(payload);
  }
  function handleConfirmBuy() {
    if (!confirming || submittingRef.current) return;
    submittingRef.current = true;
    if (confirming.kind === "character") onBuyCharacter(confirming.id);
    else onBuy(confirming.id);
    closeConfirm();
  }
  return (
    <>
    <div data-testid="oink-shop" className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      {/* Fixed height (not max-height): previously the modal sized itself to whichever
          tab's content was tallest, so switching from a 6-item tab (Head) to a 2-item tab
          (Ears) visibly shrank the whole card and the header/close button jumped. A fixed
          height pins the header, tab row, and disclaimer in place at a constant screen
          position; only the item grid area between them grows or shrinks its CONTENT,
          scrolling internally rather than resizing the shop around it. max-h-[640px] caps
          it on tall screens; no min-height floor, so h-[85vh] still shrinks safely on short
          landscape phones instead of overflowing the viewport. min-h-0 on the grid below is
          required — without it, a flex child's default min-height:auto stops it from
          shrinking below its content size, which silently breaks the internal scroll. */}
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm md:max-w-2xl p-4 h-[85vh] max-h-[640px] flex flex-col">
        <div className="flex items-center gap-2 mb-1 flex-shrink-0">
          <div className="flex-1 min-w-0">
            <div className="font-extrabold text-slate-900 text-lg leading-tight">{ct("oink_shop.title", "Oink Shop")}</div>
            <div className="text-xs text-slate-500 font-medium">Dress up {pigName} & your avatar</div>
          </div>
          <span className="inline-flex items-center gap-1.5 bg-pink-50 text-pink-600 font-bold text-sm rounded-xl pl-1.5 pr-2.5 py-1 flex-shrink-0">
            <OinkIcon className="w-6 h-6" /> {fmtNumber(balance)}
          </span>
          <button aria-label="Close" className="text-slate-400 px-1.5 flex-shrink-0" onClick={onClose}><Icon id="ic-close" size={18} style={{ color: "currentColor" }} /></button>
        </div>
        <div className="flex gap-1.5 my-2.5 flex-wrap flex-shrink-0">
          {[...OINK_SLOTS, ...(siblings.length > 0 && onGift ? [{ id: "gifts", label: ct("kid.oink_gift.tab", "Gifts"), emoji: "🎁", slots: [] }] : [])].map((s) => (
            <button key={s.id} onClick={() => setSlot(s.id)} data-testid={s.id === "gifts" ? "oink-gifts-tab" : undefined} className={`text-xs font-extrabold rounded-lg px-3 py-1.5 ${slot === s.id ? "bg-pink-500 text-white" : "bg-slate-100 text-slate-500"}`}>
              {s.label}
            </button>
          ))}
        </div>
        {slot === "gifts" ? (
          <OinkGiftsPanel child={child} siblings={siblings} onGift={onGift} />
        ) : isCharactersTab ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 auto-rows-min content-start overflow-y-auto flex-1 min-h-0 pr-0.5">
            <p className="col-span-full text-xs text-slate-400 font-medium -mt-0.5 mb-0.5">
              {young
                ? ct("oink_shop.wardrobe_note_young", "Your clothes fit every friend, so dress them all up.")
                : ct("oink_shop.wardrobe_note_older", "Everything you own fits every friend, so your wardrobe is never wasted.")}
            </p>
            {/* Piggington: free, default, always owned — first tile, never a locked/greyed
                state, and never removable (App.jsx's onEquipCharacter always allows null). */}
            <div data-testid="oink-item-pg-base" className={`rounded-2xl border p-2.5 text-center relative ${equippedChar === null ? "border-pink-400 bg-pink-50" : "border-slate-100"}`}>
              {equippedChar === null && <span className="absolute top-1.5 right-1.5 text-xs font-bold bg-pink-500 text-white rounded px-2 py-0.5">{ct("oink_shop.wearing", "Wearing")}</span>}
              <div className="h-16 flex items-center justify-center">
                <Piggington expression="fx-neutral" cosmetics={worn} className="h-16 w-auto" />
              </div>
              <div className="text-xs font-bold text-slate-700 mt-0.5 truncate">{characterName("pg-base")}</div>
              <div className="mt-2">
                {equippedChar === null ? (
                  <div className="text-xs font-medium text-slate-400 py-1.5">{ct("oink_shop.always_yours", "Always yours")}</div>
                ) : (
                  <button className="w-full bg-white border-2 border-pink-400 text-pink-500 font-extrabold text-xs rounded-xl py-1.5" onClick={() => onEquipCharacter(null)}>{ct("oink_shop.wear_it", "Wear it")}</button>
                )}
              </div>
            </div>
            {CHARACTER_CATALOG.map((ch, i) => {
              const isOwned = ownedChars.includes(ch.id);
              const isEquipped = equippedChar === ch.id;
              const affordable = balance >= ch.price;
              const showTierHeading = i === 0 || CHARACTER_CATALOG[i - 1].tier !== ch.tier;
              return (
                <React.Fragment key={ch.id}>
                  {showTierHeading && (
                    <div key={`tier-${ch.tier}`} className="col-span-full text-xs font-medium text-slate-400 mt-1">{ch.tier}</div>
                  )}
                  <div key={ch.id} data-testid={`oink-item-${ch.id}`} className={`rounded-2xl border p-2.5 text-center relative ${isEquipped ? "border-pink-400 bg-pink-50" : "border-slate-100"}`}>
                    {isEquipped && <span className="absolute top-1.5 right-1.5 text-xs font-bold bg-pink-500 text-white rounded px-2 py-0.5">{ct("oink_shop.wearing", "Wearing")}</span>}
                    {!isOwned && <span className="absolute top-1.5 right-1.5 text-xs font-bold bg-slate-200 text-slate-500 rounded px-2 py-0.5">{ct("oink_shop.preview_badge", "Preview")}</span>}
                    {/* opacity only, no grayscale — see the ChildView-level comment on the
                        Hands-tab render below for why: grayscale crushes a same-hue item
                        against the body to invisible, and a character's OWN skin colour is
                        exactly this same risk (a white unicorn desaturated is still white,
                        but a light item worn on a light character isn't guaranteed safe). */}
                    <div className={`h-16 flex items-center justify-center ${!isOwned && !affordable ? "opacity-50" : ""}`}>
                      <Piggington expression="fx-neutral" cosmetics={worn} base={ch.id} className="h-16 w-auto" />
                    </div>
                    <div className="text-xs font-bold text-slate-700 mt-0.5 truncate">{characterName(ch.id)}</div>
                    <div className="mt-2">
                      {isEquipped ? (
                        <button className="text-xs font-bold text-slate-400 py-1.5" onClick={() => onEquipCharacter(null)}>{ct("oink_shop.take_off", "Take off")}</button>
                      ) : isOwned ? (
                        <button className="w-full bg-white border-2 border-pink-400 text-pink-500 font-extrabold text-xs rounded-xl py-1.5" onClick={() => onEquipCharacter(ch.id)}>{ct("oink_shop.wear_it", "Wear it")}</button>
                      ) : affordable ? (
                        <button className="w-full bg-pink-500 text-white font-extrabold text-xs rounded-xl py-1.5 inline-flex items-center justify-center gap-1" onClick={() => openConfirm({ kind: "character", id: ch.id, price: ch.price, label: characterName(ch.id), preview: <Piggington expression="fx-neutral" cosmetics={worn} base={ch.id} className="h-16 w-auto" /> })}>
                          <OinkIcon className="w-4 h-4" /> {ch.price}
                        </button>
                      ) : (
                        <>
                          <ShopProgressBar balance={balance} price={ch.price} />
                          {(() => { const d = oinksToGo(child, ch.price); return d ? (
                            <div className="text-xs font-medium text-slate-500 mt-1" data-testid="oinks-to-go">
                              {d.toGo <= 15 ? ct("shop.nearly", "Nearly there: {n} Oinks to go", { n: d.toGo }) : ct("shop.to_go", "{n} Oinks to go, about {weeks} at your pace", { n: d.toGo, weeks: d.weeks === 1 ? ct("shop.week_one", "a week") : ct("shop.weeks", "{w} weeks", { w: d.weeks }) })}
                            </div>
                          ) : null; })()}
                        </>
                      )}
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 auto-rows-min content-start overflow-y-auto flex-1 min-h-0 pr-0.5">
            {items.map((item) => {
              const isOwned = owned.includes(item.id);
              const isEquipped = equippedCosmetic(child, item.slot) === item.id;
              const affordable = balance >= item.price;
              return (
                <div key={item.id} data-testid={`oink-item-${item.id}`} className={`rounded-2xl border p-2.5 text-center relative ${isEquipped ? "border-pink-400 bg-pink-50" : "border-slate-100"}`}>
                  {isEquipped && <span className="absolute top-1.5 right-1.5 text-xs font-bold bg-pink-500 text-white rounded px-2 py-0.5">{ct("oink_shop.wearing", "Wearing")}</span>}
                  {!isOwned && <span className="absolute top-1.5 right-1.5 text-xs font-bold bg-slate-200 text-slate-500 rounded px-2 py-0.5">{ct("oink_shop.preview_badge", "Preview")}</span>}
                  {PIGGINGTON_WARDROBE_SLOTS.includes(item.slot) ? (
                    // A real render of the item on the actual sprite — not an emoji standing
                    // in for it. Staging feedback (Aug 2026): the shop's emoji previews looked
                    // nothing like the real cosmetic once worn (e.g. a navy bowler hat in the
                    // art vs. a generic gold top-hat emoji here), so what a parent/child saw in
                    // the shop didn't match what actually appeared on Piggington.
                    //
                    // Bugfix (Aug 2026, characters/economy follow-up): opacity only here, NOT
                    // grayscale — cos-coin's fill (#FFE39B) is a near-match for the body's own
                    // #FFC629, so full desaturation crushed the one hue difference that made
                    // it visible at all, leaving it looking blank at the "unaffordable" state
                    // (bug report: "invisible... in tiles and preview"). Reduced opacity alone
                    // still reads as "locked" without erasing low-contrast items like this one.
                    <div className={`h-16 flex items-center justify-center ${!isOwned && !affordable ? "opacity-50" : ""}`}>
                      <Piggington expression="fx-neutral" cosmetics={[item.id]} base={equippedCharacterBaseId(child)} className="h-16 w-auto" />
                    </div>
                  ) : (
                    // LB-162: frames and parties are effects, not garments, so the tile shows
                    // Piggington with the effect applied — a ring for a frame, a burst of that
                    // party's pieces for a celebration — rather than an emoji standing in.
                    <div className={`h-16 flex items-center justify-center ${!isOwned && !affordable ? "opacity-50" : ""}`}>
                      {item.slot === "frame"
                        ? <span className="inline-flex rounded-full p-[3px]" style={{ background: AVATAR_FRAME_STYLES[item.id]?.ring || "#D9DEE8" }}><span className="rounded-full bg-white flex items-center justify-center w-12 h-12 overflow-hidden"><Piggington expression="fx-neutral" className="h-11 w-auto" /></span></span>
                        : <span className="relative inline-flex items-center justify-center"><Piggington expression="fx-happy" className="h-14 w-auto" />{(PARTY_PIECE_SETS[item.id] || []).slice(0, 3).map((piece, i) => <span key={i} className="absolute" style={{ top: [-2, 6, 0][i], left: [-10, 44, 30][i] }}>{piece}</span>)}</span>}
                    </div>
                  )}
                  <div className="text-xs font-bold text-slate-700 mt-0.5 truncate">{item.label}</div>
                  <div className="mt-2">
                    {isEquipped ? (
                      <button className="text-xs font-bold text-slate-400 py-1.5" onClick={() => onEquip(item.slot, null)}>{ct("oink_shop.take_off", "Take off")}</button>
                    ) : isOwned ? (
                      <button className="w-full bg-white border-2 border-pink-400 text-pink-500 font-extrabold text-xs rounded-xl py-1.5" onClick={() => onEquip(item.slot, item.id)}>{ct("oink_shop.wear_it", "Wear it")}</button>
                    ) : affordable ? (
                      <button className="w-full bg-pink-500 text-white font-extrabold text-xs rounded-xl py-1.5 inline-flex items-center justify-center gap-1" onClick={() => openConfirm({
                        kind: "cosmetic", id: item.id, price: item.price, label: item.label,
                        preview: PIGGINGTON_WARDROBE_SLOTS.includes(item.slot)
                          ? <Piggington expression="fx-neutral" cosmetics={[item.id]} base={equippedCharacterBaseId(child)} className="h-16 w-auto" />
                          : item.slot === "frame"
                          ? <span className="inline-flex rounded-full p-[3px]" style={{ background: AVATAR_FRAME_STYLES[item.id]?.ring || "#D9DEE8" }}><span className="rounded-full bg-white flex items-center justify-center w-12 h-12 overflow-hidden"><Piggington expression="fx-neutral" className="h-11 w-auto" /></span></span>
                          : <span className="relative inline-flex items-center justify-center"><Piggington expression="fx-happy" className="h-14 w-auto" />{(PARTY_PIECE_SETS[item.id] || []).slice(0, 3).map((piece, i) => <span key={i} className="absolute" style={{ top: [-2, 6, 0][i], left: [-10, 44, 30][i] }}>{piece}</span>)}</span>,
                      })}>
                        <OinkIcon className="w-4 h-4" /> {item.price}
                      </button>
                    ) : (
                      <ShopProgressBar balance={balance} price={item.price} />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <p className="text-xs text-slate-400 text-center pt-2.5 flex-shrink-0">Oinks are just for fun — they're never worth money.</p>
      </div>
    </div>
    {/* LB-331 confirm-before-spend sheet, variant A (Alan sign-off): price pill above the
        question, balance-after on its own line. A tap outside the card, the back gesture
        and Not now all close it with the balance unchanged — only the primary button
        below reaches onBuy/onBuyCharacter, the actual debit. */}
      {confirming && (
        <div data-testid="oink-shop-confirm" className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={closeConfirm}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-xs shadow-2xl text-center" onClick={(e) => e.stopPropagation()}>
            <div className="h-16 flex items-center justify-center mb-3">{confirming.preview}</div>
            <span className="inline-flex items-center gap-1.5 bg-pink-50 text-pink-600 font-bold text-sm rounded-xl pl-1.5 pr-2.5 py-1 mb-3">
              <OinkIcon className="w-6 h-6" /> {ct("oink_shop.confirm.price_pill", "{price} Oinks", { price: confirming.price })}
            </span>
            <p className="text-lg font-extrabold text-slate-800">{ct("oink_shop.confirm.question", "Buy {item}?", { item: confirming.label })}</p>
            <p className="text-sm text-slate-600 mt-1">{ct("oink_shop.confirm.price_line", "{price} Oinks.", { price: confirming.price })}</p>
            <p className="text-sm text-slate-600 mt-1">
              {(() => {
                const left = balance - confirming.price;
                const line = ct("oink_shop.confirm.left_line", "You'll have {left} Oinks left.", { left });
                // The seed/CMS owns the sentence, but only the number renders bold — split
                // around the value itself rather than the {left} placeholder, so this still
                // finds it however a translator reorders the words.
                const marker = String(left);
                const idx = line.indexOf(marker);
                return idx === -1 ? line : <>{line.slice(0, idx)}<span className="font-extrabold text-slate-800">{marker}</span>{line.slice(idx + marker.length)}</>;
              })()}
            </p>
            <button data-testid="oink-shop-confirm-buy" className="w-full bg-slate-900 text-white font-bold text-sm rounded-xl py-2.5 mt-4" onClick={handleConfirmBuy}>
              {ct("oink_shop.confirm.buy", "Buy {item}", { item: confirming.label })}
            </button>
            <button data-testid="oink-shop-confirm-not-now" className="w-full text-slate-600 font-semibold text-sm py-2.5 mt-1" onClick={closeConfirm}>
              {ct("oink_shop.confirm.not_now", "Not now")}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
